import { useAlerts } from "components/Alerts/AlertProvider";
import { DialogMode, DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback } from "react";

import { useRetryDocumentExtractions } from "./useRetryDocumentExtractionsMutation";

interface UseRetryDocumentExtractionProps {
  /** The retry was accepted and a job queued. Not the same as the job finishing. */
  onSuccess?: () => void;
  /**
   * The queued job reached a terminal success and its documents are ready to
   * read again. A caller already showing an extraction needs this to refetch —
   * `onSuccess` fires when the job is queued, so anything relying on it alone
   * keeps displaying the failed reading until it remounts.
   */
  onExtractionComplete?: (documentIds: string[]) => void;
}

export const useRetryDocumentExtraction = (props?: UseRetryDocumentExtractionProps) => {
  const [retryMutation, result] = useRetryDocumentExtractions({
    onSuccess: props?.onSuccess,
  });
  const { createDialog } = useMinimizedDialogs();
  const alerts = useAlerts();
  const onExtractionComplete = props?.onExtractionComplete;

  const retryDocumentExtractions = useCallback(
    async (tenantId: string, documentIds: string[], mode: DialogMode = "minimizedInTray") => {
      try {
        if (!tenantId) {
          throw new Error("No tenant ID found");
        }
        const job = await retryMutation({
          variables: {
            tenantId,
            documentIds,
          },
        });
        const jobId = job.data?.queueDocumentAIEnrichmentJobExisting.id;
        const dialogId = `document-ai-analyzing-${jobId}`;
        createDialog({
          id: dialogId,
          dialogType: DialogType.DocumentAIAnalyzing,
          props: {
            open: true,
            jobId: jobId,
            dialogId: dialogId,
            onReview: onExtractionComplete,
          },
          mode: mode,
        });
      } catch (error) {
        alerts.error("Failed to retry document extractions", error);
      }
    },
    [retryMutation, createDialog, alerts, onExtractionComplete]
  );

  return { retryDocumentExtractions, result };
};
